import { IBaseTyreOffer } from '~/purchase/types';
import { useQueryClient } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';
import { prepareSearchQuery, PurchaseQueryKeys } from '~/purchase/api';
import {
  IProductListState,
  IQuoteListState,
  ISearchTypeArgs,
} from '~/purchase/types';

export function useUpdateSearchQuery(
  customerId: string,
  searchType: ISearchTypeArgs['searchType'],
  listState: IProductListState | IQuoteListState,
) {
  const queryClient = useQueryClient();
  const searchContext = {
    customerId: customerId,
    searchType: searchType,
  };
  const updateQuery = useCallback(
    (tyreOffer: IBaseTyreOffer) => {
      if (!listState.searchQuery) {
        return;
      }
      // update concerned ean in list query result
      const searchQueryKey = PurchaseQueryKeys.search.base(
        searchContext.customerId,
        searchContext.searchType,
        prepareSearchQuery(
          searchContext.searchType,
          'LIST',
          listState.searchQuery,
          searchContext.customerId,
        ),
      );
      const currentQueryState = queryClient.getQueryState<{
        data: IBaseTyreOffer[];
      }>(searchQueryKey);

      if (isNil(currentQueryState?.data)) {
        return;
      }
      const currentData = currentQueryState.data;
      queryClient.setQueryData(
        searchQueryKey,
        {
          ...currentData,
          data: currentData.data.map((currentTyreOffer) =>
            currentTyreOffer.tyre.ean === tyreOffer.tyre.ean
              ? tyreOffer
              : currentTyreOffer,
          ),
        },
        {
          // keep same updatedAt to prevent product filter and show spinner
          updatedAt: currentQueryState.dataUpdatedAt,
        },
      );

      // update filtered product data as well since we prevented it
      const filteredQueryKey = PurchaseQueryKeys.search.filtered(
        searchContext.customerId,
        searchContext.searchType,
        listState.searchQuery,
        {
          f: listState.filters,
          s: listState.sort,
          d: currentQueryState.dataUpdatedAt,
        },
      );
      const currentFilteredState =
        queryClient.getQueryState<IBaseTyreOffer[]>(filteredQueryKey);

      if (isNil(currentFilteredState?.data)) {
        return;
      }

      const currentFilteredData = currentFilteredState.data;
      const newTyreOffers = currentFilteredData?.map((currentTyreOffer) =>
        currentTyreOffer.tyre.ean === tyreOffer.tyre.ean
          ? tyreOffer
          : currentTyreOffer,
      );
      queryClient.setQueryData(filteredQueryKey, newTyreOffers, {
        // use same updatedAt since it is used to determine if we need to scroll to top of the list
        updatedAt: currentFilteredState.dataUpdatedAt,
      });

      return tyreOffer;
    },
    [
      listState.filters,
      listState.searchQuery,
      listState.sort,
      queryClient,
      searchContext.customerId,
      searchContext.searchType,
    ],
  );

  return updateQuery;
}
